import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _inherits from "@babel/runtime/helpers/inherits";
import _createSuper from "@babel/runtime/helpers/createSuper";
import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _flattenDeep from "lodash/flattenDeep";
import _set from "lodash/set";
import _each from "lodash/each";
import _merge from "lodash/merge";
import _isArray from "lodash/isArray";
import _isNumber from "lodash/isNumber";
import _isString from "lodash/isString";
import _isNull from "lodash/isNull";
import _uniq from "lodash/uniq";
var _excluded = ["migrate", "children", "schema", "Primitive", "hooks"];
import "core-js/modules/es6.array.map.js";
import React, { useContext, useMemo } from 'react';
import pickWildcards from './lib/pick-wildcards';
import migrate, { validate, isValid } from './migrations';
import { schemaPropType } from './lib/validate-schema';
import { map } from './lib/tree-utils';
import StructuredContentProvider, { ClientContext } from './structured-content-provider';
import { wrapWithHook } from './lib/wrap-with-hook'; // PrimitiveContext is used for scenarios where a node of a given type renders
// another <StructuredContent /> component nested inside of it
//
// In that scenario, the `Primitive` prop supplied to the <StructuredContent />
// rendered at the top of the content tree should be re-supplied to any
// nested <StructuredContent /> rendered anywhere down in the content tree

var PrimitiveContext = /*#__PURE__*/React.createContext(null); // NodeContext is used to wrap each rendered Component
// with the data for the current node being rendered

export var NodeContext = /*#__PURE__*/React.createContext();
export var DocumentContext = /*#__PURE__*/React.createContext();

var StructuredContent = function StructuredContent(_ref) {
  var shouldMigrate = _ref.migrate,
      children = _ref.children,
      schemaProp = _ref.schema,
      PrimitiveProp = _ref.Primitive,
      _ref$hooks = _ref.hooks,
      hooks = _ref$hooks === void 0 ? {} : _ref$hooks,
      props = _objectWithoutProperties(_ref, _excluded);

  var _useContext = useContext(ClientContext),
      schemaFromContext = _useContext.schema,
      componentsStore = _useContext.componentsStore;

  var schema = schemaProp || schemaFromContext; // migrate content if props.migrate is true
  // validate the content can be rendered
  // throws an error if validation fails

  var content = useMemo(function () {
    if (!shouldMigrate) {
      // if we shouldn't migrate, validate children and throw an error if validation fails
      wrapWithHook(function () {
        return validate(children, schema);
      }, hooks === null || hooks === void 0 ? void 0 : hooks.validate);
      return children;
    } // if we should migrate, check to see if it's valid and migrate if it is not


    if (wrapWithHook(function () {
      return isValid(children, schema);
    }, hooks === null || hooks === void 0 ? void 0 : hooks.validate)) {
      return children;
    }

    var migrated = wrapWithHook(function () {
      return migrate(children, schema);
    }, hooks === null || hooks === void 0 ? void 0 : hooks.migrate); // if we did migrate, validate the migrated results

    wrapWithHook(function () {
      return validate(migrated, schema);
    }, hooks === null || hooks === void 0 ? void 0 : hooks.validate);
    return migrated;
  }, [children, hooks === null || hooks === void 0 ? void 0 : hooks.migrate, hooks === null || hooks === void 0 ? void 0 : hooks.validate, schema, shouldMigrate]); // preload all components in content tree

  var components = useMemo(function () {
    // get Component reference for each unique type
    var types = _uniq(map(content, schema, function (_ref2) {
      var type = _ref2.type;
      return type;
    }));

    var treeComponents = types.reduce(function (memo, type) {
      var Component = componentsStore.load(type);
      return _objectSpread(_objectSpread({}, memo), {}, _defineProperty({}, type, Component));
    }, {});
    return treeComponents;
  }, [content, schema, componentsStore]); // determine the Primitive component used to render raw strings and numbers in the content

  var PrimitiveFromContext = useContext(PrimitiveContext); // PrimitiveFromContext exists when <StructuredContent /> is being rendered somewhere
  // nested in another <StructuredContent /> Component
  //
  // PrimitiveFromContext does NOT exist when this is the
  // top-level <StructuredContent /> component

  var Primitive = PrimitiveFromContext || PrimitiveProp; // Put the Primitive in context, then render the content

  return /*#__PURE__*/React.createElement(PrimitiveContext.Provider, {
    value: Primitive
  }, /*#__PURE__*/React.createElement(DocumentContext.Provider, {
    value: content
  }, /*#__PURE__*/React.createElement(StructuredContentImpl, _extends({}, props, {
    schema: schema,
    Primitive: Primitive,
    components: components,
    hooks: hooks
  }), content)));
};

StructuredContent.defaultProps = {
  renderNode: function renderNode() {},
  Primitive: function Primitive(_ref3) {
    var children = _ref3.children;
    return children;
  },
  migrate: true
};

if (process.env.NODE_ENV !== 'production') {
  var PropTypes = require('prop-types'); // eslint-disable-line global-require


  var structuredContentNodePropType = PropTypes.shape({
    type: PropTypes.string.isRequired,
    version: PropTypes.string.isRequired,
    props: PropTypes.object.isRequired // eslint-disable-line react/forbid-prop-types

  });
  var structuredContentPropType = PropTypes.oneOfType([structuredContentNodePropType, PropTypes.arrayOf(PropTypes.oneOfType([structuredContentNodePropType, PropTypes.string, PropTypes.number, // recursive array prop-type checker
  PropTypes.arrayOf(function () {
    return function () {
      return structuredContentPropType;
    };
  })]).isRequired)]);
  StructuredContent.propTypes = {
    /**
     * (Default supplied by `SchemaContext.Consumer`)
     */
    schema: schemaPropType,

    /**
     * JSON document to render
     */
    children: structuredContentPropType.isRequired,

    /**
     * Component to render primitive strings and numbers
     * Defaults to `Type` from src/components/Type
     */
    Primitive: PropTypes.elementType,

    /**
     * Optional method to to modify what is rendered for each type
     */
    renderNode: PropTypes.func,

    /**
     * Automatically migrate documents to schema before rendering
     */
    migrate: PropTypes.bool,

    /**
     * Enables performance monitoring of the given functions
     */
    hooks: PropTypes.shape({
      validate: PropTypes.func,
      migrate: PropTypes.func,
      renderNode: PropTypes.func
    })
  };
}

StructuredContent.Provider = StructuredContentProvider;
export default StructuredContent;

var StructuredContentImpl = /*#__PURE__*/function (_React$Component) {
  _inherits(StructuredContentImpl, _React$Component);

  var _super = _createSuper(StructuredContentImpl);

  function StructuredContentImpl() {
    _classCallCheck(this, StructuredContentImpl);

    return _super.apply(this, arguments);
  }

  _createClass(StructuredContentImpl, [{
    key: "shouldComponentUpdate",
    value: function shouldComponentUpdate(nextProps) {
      var _this$props = this.props,
          schema = _this$props.schema,
          children = _this$props.children,
          renderNode = _this$props.renderNode,
          Primitive = _this$props.Primitive,
          components = _this$props.components;
      if (schema !== nextProps.schema) return true;
      if (children !== nextProps.children) return true;
      if (Primitive !== nextProps.Primitive) return true;
      if (renderNode !== nextProps.renderNode) return true;
      if (components !== nextProps.components) return true;
      return false;
    } // this is defined and noop-ed
    // to use in test assertions

  }, {
    key: "componentWillUnmount",
    value: function componentWillUnmount() {} // it's turtles all the way down

  }, {
    key: "createRecursiveNode",
    value: function createRecursiveNode(node, _ref4) {
      var _this = this;

      var renderNode = _ref4.renderNode,
          path = _ref4.path;
      var components = this.props.components;
      var _this$props2 = this.props,
          schema = _this$props2.schema,
          Primitive = _this$props2.Primitive; // render leafs

      if (_isNull(node)) return null;
      if (_isString(node)) return /*#__PURE__*/React.createElement(Primitive, {
        key: path
      }, node);
      if (_isNumber(node)) return /*#__PURE__*/React.createElement(Primitive, {
        key: path
      }, node); // render lists

      if (_isArray(node)) {
        return node.map(function (n, i) {
          // keep track of path in list
          var arrayPath = path ? "".concat(path, ".").concat(i) : "".concat(i);
          return _this.createRecursiveNode(n, {
            renderNode: renderNode,
            path: arrayPath
          });
        });
      } // render structured-content json objects


      var type = node.type,
          props = node.props;
      var nodeProps = schema[type].nodeProps;

      var propsToRender = _merge({
        key: path
      }, props); // start to build key for child nodes


      var propsPath = path ? "".concat(path, ".props") : 'props'; // recursively render any props that are `PropTypes.node` as StructuredContentNode
      // and merge those recursively rendered structured props to final props to be rendered

      _each(pickWildcards(props, nodeProps), function (propValue, propPath) {
        var renderNodeProp = function renderNodeProp(childNode, i) {
          // keep track of path in node tree
          var childPath = "".concat(propsPath, ".").concat(propPath, ".").concat(i); // render child nodes

          return _this.createRecursiveNode(childNode, {
            renderNode: renderNode,
            path: childPath
          });
        }; // recursively render child structured-content nodes


        _set(propsToRender, propPath, _flattenDeep([propValue]).map(renderNodeProp));
      }); // render this node in its component


      var Component = components[type];
      var hooks = this.props.hooks;
      var renderedNode = wrapWithHook(function () {
        return renderNode(_objectSpread(_objectSpread({}, node), {}, {
          props: propsToRender
        }), Component);
      }, hooks === null || hooks === void 0 ? void 0 : hooks.renderNode) || /*#__PURE__*/React.createElement(Component, propsToRender);
      return /*#__PURE__*/React.createElement(NodeContext.Provider, {
        value: {
          node: node,
          path: path
        },
        key: path
      }, renderedNode);
    }
  }, {
    key: "render",
    value: function render() {
      var _this$props3 = this.props,
          renderNode = _this$props3.renderNode,
          children = _this$props3.children;
      return this.createRecursiveNode(children, {
        renderNode: renderNode
      });
    }
  }]);

  return StructuredContentImpl;
}(React.Component);

_defineProperty(StructuredContentImpl, "defaultProps", _objectSpread({}, StructuredContent.defaultProps));

if (process.env.NODE_ENV !== 'production') {
  var _PropTypes = require('prop-types'); // eslint-disable-line global-require
  // eslint-disable-next-line react/static-property-placement


  StructuredContentImpl.propTypes = _objectSpread(_objectSpread({}, StructuredContent.propTypes), {}, {
    /**
     * (internal) map of { [TYPE]: ComponentForType } of components loaded into componentsStore
     */
    components: _PropTypes.objectOf(_PropTypes.elementType).isRequired
  });
} // eslint-disable-next-line no-underscore-dangle


var __forTests = {
  StructuredContentImpl: StructuredContentImpl
};
export { __forTests };